/**
 * Local persistence for the match *setup*, so a reload mid-match knows whose balls it just
 * read off disk.
 *
 * Without this the ledger survives a reload and the match does not: 250 events and no rosters,
 * no toss, no RuleSet — which is to say no scorecard. "Lose nothing" has to include the frame
 * the events are interpreted in.
 *
 * The `current` pointer is what makes recovery automatic rather than a menu the scorer has to
 * find. A device is scoring one match at a time; on reload it resumes that one.
 */
import type { MatchId } from "@sportsos/engine";
import type { MatchSetup } from "../match/setup.ts";
import { CURRENT, MATCHES, asPromise, committed, database } from "./db.ts";

const CURRENT_KEY = "matchId";

interface SetupRow {
  readonly matchId: string;
  readonly setup: MatchSetup;
}

/** Write the setup and mark it current, in one transaction so they cannot disagree. */
export async function saveSetup(setup: MatchSetup): Promise<void> {
  const db = await database();
  const tx = db.transaction([MATCHES, CURRENT], "readwrite");
  tx.objectStore(MATCHES).put({ matchId: setup.matchId, setup } satisfies SetupRow);
  tx.objectStore(CURRENT).put(setup.matchId, CURRENT_KEY);
  await committed(tx);
}

/** The match this device was scoring, or null for a clean start. */
export async function loadCurrentSetup(): Promise<MatchSetup | null> {
  const db = await database();
  const tx = db.transaction([MATCHES, CURRENT], "readonly");
  const matchId = await asPromise<string | undefined>(tx.objectStore(CURRENT).get(CURRENT_KEY));
  if (matchId === undefined) {
    await committed(tx);
    return null;
  }
  const row = await asPromise<SetupRow | undefined>(tx.objectStore(MATCHES).get(matchId));
  await committed(tx);
  return row?.setup ?? null;
}

/** The saved setup for one match, whether or not it is the current one. Null if this device never had it. */
export async function loadSetup(matchId: string): Promise<MatchSetup | null> {
  const db = await database();
  const tx = db.transaction(MATCHES, "readonly");
  const row = await asPromise<SetupRow | undefined>(tx.objectStore(MATCHES).get(matchId));
  await committed(tx);
  return row?.setup ?? null;
}

/**
 * Make a match this device already holds the one it is scoring, and hand back its setup.
 *
 * **Only the pointer moves.** Each match's setup is its own row and each ball is stored under
 * its own `matchId`, so the match being left keeps everything — unsent balls included, and
 * they still send in the background. Nothing is copied, merged or deleted, which is what lets
 * a scorer run two matches from one phone and move between them. Null, and nothing changed,
 * if this device has no setup for that match.
 *
 * One transaction, so a crash cannot leave the pointer naming a match with no setup.
 */
export async function makeCurrent(matchId: string): Promise<MatchSetup | null> {
  const db = await database();
  const tx = db.transaction([MATCHES, CURRENT], "readwrite");
  const row = await asPromise<SetupRow | undefined>(tx.objectStore(MATCHES).get(matchId));
  if (row !== undefined) tx.objectStore(CURRENT).put(matchId, CURRENT_KEY);
  await committed(tx);
  return row?.setup ?? null;
}

/**
 * Stop resuming this match, without deleting it.
 *
 * "New match" must not destroy the old one. Its ledger may still have unsynced balls in it, and
 * throwing those away to make a menu tidier is the failure this whole feature exists to
 * prevent — so only the pointer is cleared.
 */
export async function clearCurrent(): Promise<void> {
  const db = await database();
  const tx = db.transaction(CURRENT, "readwrite");
  tx.objectStore(CURRENT).delete(CURRENT_KEY);
  await committed(tx);
}

export type { MatchId };
